// src/store/slices/balanceSlice.ts

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from '../index';
import { subscribeToUserBalance } from '../../firebase';
import { RootState } from '../index';

interface BalanceState {
  ethBalance: number;
  eurBalance: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: BalanceState = {
  ethBalance: 0,
  eurBalance: 0,
  isLoading: true,
  error: null,
};

// Keep track of the current subscription
let currentBalanceUnsubscribe: (() => void) | null = null;

// Set up a Firestore balance subscription, dispatching updates into the store.
export const subscribeToBalance = (userEmail: string) => (dispatch: AppDispatch) => {
  // Clean up existing subscription if any
  if (currentBalanceUnsubscribe) {
    currentBalanceUnsubscribe();
  }

  dispatch(startLoading());

  currentBalanceUnsubscribe = subscribeToUserBalance(
    userEmail,
    (balanceData) => {
      dispatch(setBalance(balanceData));
    },
    (error) => {
      dispatch(setError(error.message));
    }
  );
};

export const cleanupBalanceSubscription = () => {
  if (currentBalanceUnsubscribe) {
    currentBalanceUnsubscribe();
    currentBalanceUnsubscribe = null;
  }
};

const balanceSlice = createSlice({
  name: 'balance',
  initialState,
  reducers: {
    setBalance: (state, action: PayloadAction<{ ethBalance: number; eurBalance: number }>) => {
      state.ethBalance = action.payload.ethBalance;
      state.eurBalance = action.payload.eurBalance;
      state.isLoading = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    startLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
  },
});

export const { setBalance, setError, startLoading } = balanceSlice.actions;

// Selectors
const selectBalanceState = (state: RootState) => state.balanceSlice;

export const selectBalance = createSelector(
  [selectBalanceState],
  (balanceState) => ({
    ethBalance: balanceState.ethBalance,
    eurBalance: balanceState.eurBalance,
    isLoading: balanceState.isLoading,
    error: balanceState.error
  })
);

export const selectEthBalance = createSelector(
  [selectBalanceState],
  (balanceState) => balanceState.ethBalance
);

export default balanceSlice.reducer;